import { getDeleteApiGroupIdMutationOptions, type GroupViewDTO } from '@smartsash/api-client';
import { useEntityDelete } from '@smartsash/server-ops';
import { Icon, openModal } from '@smartsash/ui';
import { useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ADMIN_LIST_SEARCH_DEFAULTS } from '@/router/listSearch';
import { DEVICES_GROUP_FIELD, devicesFilteredBy } from '@/ui/pages/Devices/devicesModel';
import { USERS_GROUP_FIELD, usersFilteredBy } from '@/ui/pages/Users/usersModel';
import { RowActions as Shared, type RowActionEntry } from '@/ui/shared/RowActions';

function RowActions({ row }: { row: GroupViewDTO }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const id = row.id ?? '';
  // HS-847: delete via server-ops so the group list query is invalidated (list
  // refreshes in place) and a toast is emitted through the shared sink.
  const del = useEntityDelete({
    entity: 'group',
    mutationOptions: getDeleteApiGroupIdMutationOptions,
    // HS-1141: the deleted record's own reads are dropped, not refetched into a 404.
    deletesRecord: true,
  });

  // HS-1177: the imperative modal replaces a `useState` pair plus a conditionally
  // rendered dialog. It resolves with the pressed action's `value`, or `null` when
  // dismissed via the overlay, Escape or the close button.
  const handleDelete = useCallback(async () => {
    const { action } = await openModal({
      title: t('groups.deleteTitle', { name: row.name }),
      description: t('groups.deleteDescription'),
      variant: 'error',
      icon: <Icon name="trash-01" />,
      actions: [
        { label: t('common.cancel'), value: 'cancel' },
        { label: t('common.delete'), value: 'confirm', destructive: true },
      ],
    }).closed;

    if (action === 'confirm') del.mutate({ id: id });
  }, [del, id, row.name, t]);

  const items: RowActionEntry[] = [
    {
      label: t('common.edit'),
      permission: 'group.edit',
      icon: 'edit-01',
      onClick: () => {
        void navigate({ to: '/groups/$id/edit', params: { id } });
      },
    },
    null,
    {
      label: t('groups.actions.viewLaboratories'),
      icon: 'lab',
      onClick: () => {
        void navigate({
          to: '/laboratories',
          search: { ...ADMIN_LIST_SEARCH_DEFAULTS, groupId: id },
        });
      },
    },
    {
      label: t('groups.actions.viewDevices'),
      icon: 'tv-01',
      onClick: () => {
        // HS-1510: the note here used to say `GetApiDeviceParams` has no group filter (HS-869).
        // It has one — `groupId` — and `GroupDetails` has been using it since HS-1290; only this
        // list's menu was never revisited, so the shortcut showed every device in the system.
        void navigate({ to: '/devices', search: devicesFilteredBy(DEVICES_GROUP_FIELD, id) });
      },
    },
    {
      label: t('groups.actions.viewUsers'),
      icon: 'users-02',
      onClick: () => {
        // HS-1240: `GET /api/User/list` takes the group as a GUID, matched on the user's active
        // assignments — see `usersFilteredBy`. The old `GET /api/User` had no group filter at all.
        void navigate({ to: '/users', search: usersFilteredBy(USERS_GROUP_FIELD, id) });
      },
    },
    null,
    {
      label: t('common.delete'),
      permission: 'group.delete',
      icon: 'trash-01',
      onClick: () => {
        void handleDelete();
      },
    },
  ];

  return (
    <>
      <Shared items={items} />
    </>
  );
}

export default RowActions;
